import axios from "axios"
import { Core } from "~/core/Core"
import { CookieHelper } from "~/utils/CookieHelper"
import { LocalStorageHelper } from "~/utils/LocalStorageHelper"
import { TimeHelper } from "~/utils/TimeHelper"

interface TokenPair {
  token: string
  refresh_token: string
}

let pendingRefresh: Promise<string> | null = null
let lastReplacedToken = ""
let replacementToken = ""

export class AuthSession {
  static get storedMe(): any {
    return LocalStorageHelper.get("me") || {}
  }

  static get cookieMe(): any {
    try {
      return JSON.parse(CookieHelper.get("me") || "{}")
    } catch (_) {
      return {}
    }
  }

  static canRefresh(token: string): boolean {
    if (typeof window === "undefined" || Core.config.AUTHENTICATION_PATH === "ecom") return false
    const me = this.storedMe
    return !!token && !!me?.refresh_token && this.cookieMe.auth_type !== "azure_ad" &&
      (me.token === token || (lastReplacedToken === token && me.token === replacementToken))
  }

  static async refresh(expiredToken: string): Promise<string> {
    const current = this.storedMe
    if (current.token && current.token !== expiredToken) {
      if (lastReplacedToken === expiredToken && current.token === replacementToken) {
        return current.token
      }
      throw new Error("Login session changed before token refresh")
    }
    if (!pendingRefresh) {
      pendingRefresh = this.requestNewTokens(current.refresh_token)
        .finally(() => { pendingRefresh = null })
    }
    return pendingRefresh
  }

  private static async requestNewTokens(oldRefreshToken: string): Promise<string> {
    const response = await axios.post<TokenPair>(
      Core.MKTBACKENDAPI("/login/refresh-token"),
      { refresh_token: oldRefreshToken },
      { headers: { "x-timestamp": TimeHelper.toUTC(TimeHelper.getCurrentDateTime()) } }
    )
    const tokens = response.data
    if (!tokens?.token || !tokens?.refresh_token) {
      throw new Error("Refresh response did not contain both tokens")
    }

    // Logout or a new login may have happened while the request was in flight.
    const me = this.storedMe
    if (me.refresh_token !== oldRefreshToken) {
      throw new Error("Login session changed during token refresh")
    }
    const updated = { ...me, token: tokens.token, refresh_token: tokens.refresh_token }
    lastReplacedToken = me.token
    replacementToken = tokens.token
    LocalStorageHelper.set("me", updated)
    CookieHelper.set("me", JSON.stringify({ token: tokens.token }))

    // Nuxt's store is available only in the browser; refresh must also work
    // when the requester is used before a page component is mounted.
    const nuxt = (window as any).$nuxt
    // The stored /me response keeps permissions as an array, while the sidebar
    // uses the normalized permission map in Vuex. Only replace the token pair.
    nuxt?.$store?.commit("app/updateState", {
      key: "me",
      data: { token: tokens.token, refresh_token: tokens.refresh_token },
    })
    return tokens.token
  }

  static expireSessionForToken(token: string): void {
    if (this.storedMe.token !== token) return
    CookieHelper.remove("me")
    LocalStorageHelper.set("me", null)
    const nuxt = (window as any).$nuxt
    nuxt?.$store?.commit("app/replaceState", { key: "me", data: null })
    const redirect = `${window.location.pathname}${window.location.search}`
    window.location.assign(`/login?redirect=${encodeURIComponent(redirect)}`)
  }
}
